import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export type CarnivalArtDiagnostics = {
  source: 'procedural';
  meshes: number;
  instancedMeshes: number;
  geometries: number;
  materials: number;
  textures: number;
  shadowCasters: number;
  animatedProps: number;
  bulbInstances: number;
  confettiCapacity: number;
  puffCapacity: number;
};

export type CarnivalPoint = Readonly<{ x: number; y: number; z: number }>;

type ArtMaterials = {
  navy: THREE.MeshStandardMaterial;
  deepNavy: THREE.MeshStandardMaterial;
  red: THREE.MeshStandardMaterial;
  redCloth: THREE.MeshStandardMaterial;
  cream: THREE.MeshStandardMaterial;
  creamCloth: THREE.MeshStandardMaterial;
  gold: THREE.MeshStandardMaterial;
  marqueeGlow: THREE.MeshStandardMaterial;
  teal: THREE.MeshStandardMaterial;
  tealCloth: THREE.MeshStandardMaterial;
  pink: THREE.MeshStandardMaterial;
  purple: THREE.MeshStandardMaterial;
  mint: THREE.MeshStandardMaterial;
  orange: THREE.MeshStandardMaterial;
  skyBlue: THREE.MeshStandardMaterial;
  wood: THREE.MeshStandardMaterial;
  darkWood: THREE.MeshStandardMaterial;
  darkMetal: THREE.MeshStandardMaterial;
  ink: THREE.MeshBasicMaterial;
  crowd: THREE.MeshBasicMaterial;
};

type AnimatedProp = {
  root: THREE.Object3D;
  baseY: number;
  baseRotationY: number;
  phase: number;
  bob: number;
  spin: number;
};

type BuntingFlag = {
  position: THREE.Vector3;
  material: THREE.Material;
  width: number;
  height: number;
  baseRotation: number;
  phase: number;
  mesh?: THREE.InstancedMesh;
  instanceIndex?: number;
};

type CrowdFigure = {
  x: number;
  z: number;
  scale: number;
  phase: number;
  baseRotation: number;
};

type ConfettiState = {
  active: boolean;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  rotation: THREE.Euler;
  spin: THREE.Vector3;
  life: number;
  maxLife: number;
};

type PuffState = {
  active: boolean;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
};

type RingState = {
  mesh: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  active: boolean;
  life: number;
  maxLife: number;
  size: number;
};

const CONFETTI_CAPACITY = 84;
const PUFF_CAPACITY = 36;
const RING_CAPACITY = 8;
const CONFETTI_COLORS = ['#ffcf58', '#ff4f75', '#35d7ca', '#fff2cf', '#9f7cff', '#ff8b45'];

/**
 * Self-contained art direction and event VFX for the cup-toss booth.
 *
 * The physics board is intentionally not part of this group. The open play area is
 * centered around x=0, y=2.56, z=-3.3 and remains unobstructed.
 */
export class CarnivalScene {
  readonly group = new THREE.Group();

  private readonly ownedGeometries = new Set<THREE.BufferGeometry>();
  private readonly ownedMaterials = new Set<THREE.Material>();
  private readonly ownedTextures = new Set<THREE.Texture>();
  private readonly materials: ArtMaterials;
  private readonly animatedProps: AnimatedProp[] = [];
  private readonly buntingFlags: BuntingFlag[] = [];
  private readonly buntingMeshes: THREE.InstancedMesh[] = [];
  private readonly crowdFigures: CrowdFigure[] = [];
  private readonly bulbPoints: Array<{ position: THREE.Vector3; phase: number }> = [];
  private readonly dummy = new THREE.Object3D();

  private bulbMesh!: THREE.InstancedMesh;
  private crowdBodyMesh!: THREE.InstancedMesh;
  private crowdHeadMesh!: THREE.InstancedMesh;
  private confettiMesh!: THREE.InstancedMesh;
  private puffMesh!: THREE.InstancedMesh;
  private readonly confettiStates: ConfettiState[] = [];
  private readonly puffStates: PuffState[] = [];
  private readonly ringStates: RingState[] = [];
  private readonly aimGroup = new THREE.Group();
  private readonly aimMaterials: THREE.MeshBasicMaterial[] = [];

  private nextConfetti = 0;
  private nextPuff = 0;
  private nextRing = 0;
  private aimLife = 0;
  private aimPower = 0;
  private roundOver = false;
  private disposed = false;

  constructor() {
    this.group.name = 'carnival-scene-art';
    this.materials = this.createMaterialLibrary();
    this.buildEnvironment();
    this.finishBuntingInstances();
    this.batchStaticEnvironment();
    this.finishBulbInstances();
    this.buildVfxPools();
  }

  update(delta: number, elapsed: number): void {
    if (this.disposed) return;

    this.updateBulbs(elapsed);
    this.updateAnimatedProps(elapsed);
    this.updateBunting(elapsed);
    this.updateCrowd(elapsed);
    this.updateAim(delta, elapsed);
    this.updateConfetti(delta);
    this.updatePuffs(delta);
    this.updateRings(delta);

    const signPulse = this.roundOver
      ? 1.45 + Math.sin(elapsed * 7.5) * 0.75
      : 1.05 + Math.sin(elapsed * 2.2) * 0.18;
    this.materials.marqueeGlow.emissiveIntensity = signPulse;
  }

  celebrate(position: CarnivalPoint, special = false): void {
    if (this.disposed) return;

    const count = special ? 54 : 28;
    const origin = new THREE.Vector3(position.x, position.y + 0.16, position.z);
    for (let index = 0; index < count; index += 1) {
      const slot = this.nextConfetti;
      this.nextConfetti = (this.nextConfetti + 1) % CONFETTI_CAPACITY;
      const state = this.confettiStates[slot];
      const angle = Math.random() * Math.PI * 2;
      const radial = (special ? 1.25 : 0.78) + Math.random() * (special ? 2.15 : 1.25);
      const lift = (special ? 2.8 : 1.9) + Math.random() * (special ? 3.4 : 2.1);

      state.active = true;
      state.position.copy(origin);
      state.position.x += (Math.random() - 0.5) * 0.25;
      state.position.z += (Math.random() - 0.5) * 0.25;
      state.velocity.set(Math.cos(angle) * radial, lift, Math.sin(angle) * radial);
      state.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      state.spin.set(
        (Math.random() - 0.5) * 13,
        (Math.random() - 0.5) * 13,
        (Math.random() - 0.5) * 16,
      );
      state.maxLife = 1.05 + Math.random() * (special ? 1.45 : 0.85);
      state.life = state.maxLife;
      this.confettiMesh.setColorAt(
        slot,
        new THREE.Color(CONFETTI_COLORS[(slot + (special ? index : index * 2)) % CONFETTI_COLORS.length]),
      );
    }
    if (this.confettiMesh.instanceColor) this.confettiMesh.instanceColor.needsUpdate = true;

    this.spawnRing(origin, special ? '#ff4f75' : '#ffcf58', special ? 1.45 : 1);
  }

  pulseAim(position: CarnivalPoint, power: number): void {
    if (this.disposed) return;
    this.aimPower = THREE.MathUtils.clamp(power, 0, 1);
    this.aimLife = 0.2;
    this.aimGroup.visible = true;
    this.aimGroup.position.set(position.x, position.y + 0.045, position.z);
  }

  impact(position: CarnivalPoint, strength = 1): void {
    if (this.disposed) return;
    const impulse = THREE.MathUtils.clamp(strength, 0.25, 2.2);
    const origin = new THREE.Vector3(position.x, position.y, position.z);
    const count = Math.round(5 + impulse * 4);

    for (let index = 0; index < count; index += 1) {
      const slot = this.nextPuff;
      this.nextPuff = (this.nextPuff + 1) % PUFF_CAPACITY;
      const state = this.puffStates[slot];
      const angle = (index / count) * Math.PI * 2 + Math.random() * 0.35;
      const speed = 0.35 + Math.random() * 0.5 * impulse;
      state.active = true;
      state.position.copy(origin);
      state.velocity.set(Math.cos(angle) * speed, 0.18 + Math.random() * 0.4, Math.sin(angle) * speed);
      state.maxLife = 0.28 + Math.random() * 0.22;
      state.life = state.maxLife;
      state.size = 0.08 + Math.random() * 0.1 * impulse;
      this.puffMesh.setColorAt(slot, new THREE.Color(index % 3 === 0 ? '#ffcf58' : '#fff2cf'));
    }
    if (this.puffMesh.instanceColor) this.puffMesh.instanceColor.needsUpdate = true;
    this.spawnRing(origin, '#fff2cf', impulse);
  }

  setRoundOver(over: boolean): void {
    if (this.disposed || this.roundOver === over) return;
    this.roundOver = over;
    if (over) this.celebrate({ x: 0, y: 4.65, z: -3.35 }, true);
  }

  getDiagnostics(): CarnivalArtDiagnostics {
    let meshes = 0;
    let instancedMeshes = 0;
    let shadowCasters = 0;

    this.group.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      meshes += 1;
      if (object instanceof THREE.InstancedMesh) instancedMeshes += 1;
      if (object.castShadow) shadowCasters += 1;
    });

    return {
      source: 'procedural',
      meshes,
      instancedMeshes,
      geometries: this.ownedGeometries.size,
      materials: this.ownedMaterials.size,
      textures: this.ownedTextures.size,
      shadowCasters,
      animatedProps: this.animatedProps.length,
      bulbInstances: this.bulbPoints.length,
      confettiCapacity: CONFETTI_CAPACITY,
      puffCapacity: PUFF_CAPACITY,
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.group.removeFromParent();
    for (const geometry of this.ownedGeometries) geometry.dispose();
    for (const texture of this.ownedTextures) texture.dispose();
    for (const material of this.ownedMaterials) material.dispose();
    this.ownedGeometries.clear();
    this.ownedTextures.clear();
    this.ownedMaterials.clear();
  }

  private buildEnvironment(): void {
    const world = new THREE.Group();
    world.name = 'layered-fairground-world';
    world.add(this.createNightBackdrop());
    world.add(this.createFloor());
    world.add(this.createDistantFair());
    world.add(this.createBooth());
    world.add(this.createForegroundAccents());

    const leftPractical = new THREE.PointLight('#ffbd69', 16, 9, 2);
    leftPractical.name = 'left-booth-practical';
    leftPractical.position.set(-4.6, 6.65, -1.4);
    const rightPractical = leftPractical.clone();
    rightPractical.name = 'right-booth-practical';
    rightPractical.position.x = 4.6;
    world.add(leftPractical, rightPractical);
    this.group.add(world);
  }

  private createMaterialLibrary(): ArtMaterials {
    const standard = (parameters: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial =>
      this.material(new THREE.MeshStandardMaterial(parameters));
    const basic = (parameters: THREE.MeshBasicMaterialParameters): THREE.MeshBasicMaterial =>
      this.material(new THREE.MeshBasicMaterial(parameters));

    return {
      navy: standard({ color: '#173650', roughness: 0.72, metalness: 0.03 }),
      deepNavy: standard({ color: '#081827', roughness: 0.82, metalness: 0.02 }),
      red: standard({ color: '#df3154', roughness: 0.47, metalness: 0.03 }),
      redCloth: standard({ color: '#d52c4e', roughness: 0.9, side: THREE.DoubleSide }),
      cream: standard({ color: '#fff0cb', roughness: 0.58, metalness: 0.01 }),
      creamCloth: standard({ color: '#f8dfae', roughness: 0.92, side: THREE.DoubleSide }),
      gold: standard({ color: '#ffc94f', roughness: 0.34, metalness: 0.28 }),
      marqueeGlow: standard({
        color: '#ffd362',
        roughness: 0.3,
        metalness: 0.18,
        emissive: '#bd5b13',
        emissiveIntensity: 1.1,
      }),
      teal: standard({ color: '#1ec5bb', roughness: 0.42, metalness: 0.08 }),
      tealCloth: standard({ color: '#169f9d', roughness: 0.9, side: THREE.DoubleSide }),
      pink: standard({ color: '#ff6791', roughness: 0.46, metalness: 0.02 }),
      purple: standard({ color: '#8566e8', roughness: 0.5, metalness: 0.04 }),
      mint: standard({ color: '#78e3b5', roughness: 0.52, metalness: 0.02 }),
      orange: standard({ color: '#ff8a3d', roughness: 0.55, metalness: 0.02 }),
      skyBlue: standard({ color: '#66bde8', roughness: 0.48, metalness: 0.04 }),
      wood: standard({ color: '#84502f', roughness: 0.8, metalness: 0.01 }),
      darkWood: standard({ color: '#3b2430', roughness: 0.86, metalness: 0 }),
      darkMetal: standard({ color: '#273140', roughness: 0.48, metalness: 0.42 }),
      ink: basic({ color: '#12202d' }),
      crowd: basic({ color: '#111426' }),
    };
  }

  private createNightBackdrop(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'far-night-layer';
    const texture = this.canvasTexture(1024, 512, (context, width, height) => {
      const sky = context.createLinearGradient(0, 0, 0, height);
      sky.addColorStop(0, '#050b1e');
      sky.addColorStop(0.52, '#112441');
      sky.addColorStop(1, '#452442');
      context.fillStyle = sky;
      context.fillRect(0, 0, width, height);

      const glow = context.createRadialGradient(width * 0.5, height * 0.62, 0, width * 0.5, height * 0.62, width * 0.5);
      glow.addColorStop(0, 'rgba(255, 107, 112, 0.23)');
      glow.addColorStop(0.5, 'rgba(91, 82, 179, 0.09)');
      glow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      context.fillStyle = glow;
      context.fillRect(0, 0, width, height);

      let seed = 1947;
      const random = (): number => {
        seed = (seed * 16807) % 2147483647;
        return (seed - 1) / 2147483646;
      };
      for (let index = 0; index < 145; index += 1) {
        const x = random() * width;
        const y = random() * height * 0.72;
        const radius = random() > 0.9 ? 2 : 0.65 + random() * 0.8;
        context.fillStyle = random() > 0.84 ? 'rgba(255, 213, 115, 0.9)' : 'rgba(239, 245, 255, 0.72)';
        context.beginPath();
        context.arc(x, y, radius, 0, Math.PI * 2);
        context.fill();
      }

      context.globalAlpha = 0.16;
      context.strokeStyle = '#ffba6d';
      context.lineWidth = 2;
      for (let x = 32; x < width; x += 90) {
        context.beginPath();
        context.moveTo(x, height);
        context.lineTo(x + 25, height * 0.75);
        context.lineTo(x + 50, height);
        context.stroke();
      }
      context.globalAlpha = 1;
    });
    const material = this.material(new THREE.MeshBasicMaterial({ map: texture, depthWrite: false }));
    const sky = new THREE.Mesh(this.geometry(new THREE.PlaneGeometry(38, 20)), material);
    sky.name = 'painted-night-sky';
    sky.position.set(0, 8.1, -14.2);
    sky.renderOrder = -20;
    group.add(sky);
    return group;
  }

  private createFloor(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'fairground-floor-layer';
    const floorTexture = this.canvasTexture(512, 512, (context, width, height) => {
      context.fillStyle = '#34283a';
      context.fillRect(0, 0, width, height);
      context.fillStyle = '#3c3043';
      for (let y = 0; y < height; y += 64) {
        context.fillRect(0, y, width, 31);
      }
      context.strokeStyle = 'rgba(255, 226, 180, 0.12)';
      context.lineWidth = 3;
      for (let y = 0; y <= height; y += 32) {
        context.beginPath();
        context.moveTo(0, y);
        context.lineTo(width, y);
        context.stroke();
      }
      context.strokeStyle = 'rgba(7, 10, 19, 0.32)';
      for (let x = 0; x <= width; x += 128) {
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x, height);
        context.stroke();
      }
      const flecks = ['#df3154', '#ffc94f', '#1ec5bb', '#8566e8'];
      for (let index = 0; index < 36; index += 1) {
        const x = (index * 83) % width;
        const y = (index * 137) % height;
        context.fillStyle = `${flecks[index % flecks.length]}55`;
        context.save();
        context.translate(x, y);
        context.rotate((index * 0.73) % Math.PI);
        context.fillRect(-5, -1.5, 10, 3);
        context.restore();
      }
    });
    floorTexture.wrapS = THREE.RepeatWrapping;
    floorTexture.wrapT = THREE.RepeatWrapping;
    floorTexture.repeat.set(4.2, 3.4);
    const floorMaterial = this.material(
      new THREE.MeshStandardMaterial({ color: '#58425e', map: floorTexture, roughness: 0.93, metalness: 0 }),
    );
    const floor = new THREE.Mesh(this.geometry(new THREE.PlaneGeometry(34, 28)), floorMaterial);
    floor.name = 'painted-boardwalk';
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(0, -0.035, -0.5);
    floor.receiveShadow = true;
    group.add(floor);

    const runnerTexture = this.canvasTexture(256, 512, (context, width, height) => {
      const gradient = context.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, '#0c5e66');
      gradient.addColorStop(0.18, '#169f9d');
      gradient.addColorStop(0.5, '#1db8aa');
      gradient.addColorStop(0.82, '#169f9d');
      gradient.addColorStop(1, '#0c5e66');
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);
      context.strokeStyle = '#ffd86f';
      context.lineWidth = 7;
      context.strokeRect(12, 12, width - 24, height - 24);
      context.strokeStyle = 'rgba(255, 240, 203, 0.5)';
      context.lineWidth = 2;
      context.strokeRect(24, 24, width - 48, height - 48);
      context.fillStyle = 'rgba(255, 240, 203, 0.55)';
      for (let y = 75; y < height; y += 112) {
        this.drawCanvasStar(context, width / 2, y, 17, 7, 5);
        context.fill();
      }
    });
    const runnerMaterial = this.material(
      new THREE.MeshStandardMaterial({ color: '#ffffff', map: runnerTexture, roughness: 0.9 }),
    );
    const runner = new THREE.Mesh(this.geometry(new THREE.PlaneGeometry(4.6, 10.5)), runnerMaterial);
    runner.name = 'throw-lane-runner';
    runner.rotation.x = -Math.PI / 2;
    runner.position.set(0, 0.002, 6.1);
    runner.receiveShadow = true;
    group.add(runner);
    return group;
  }

  private createDistantFair(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'distant-fair-and-crowd';
    group.add(this.createDistantBooth(-10.2, -11.35, 1.1, this.materials.purple));
    group.add(this.createDistantBooth(10.1, -11.7, 1.18, this.materials.teal));

    const headGeometry = this.geometry(new THREE.SphereGeometry(0.22, 8, 6));
    const bodyGeometry = this.geometry(new THREE.CapsuleGeometry(0.26, 0.42, 3, 7));
    this.crowdBodyMesh = new THREE.InstancedMesh(bodyGeometry, this.materials.crowd, 28);
    this.crowdBodyMesh.name = 'instanced-crowd-bodies';
    this.crowdBodyMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.crowdHeadMesh = new THREE.InstancedMesh(headGeometry, this.materials.crowd, 28);
    this.crowdHeadMesh.name = 'instanced-crowd-heads';
    this.crowdHeadMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let index = 0; index < 28; index += 1) {
      const x = -14 + index * 1.04 + Math.sin(index * 2.7) * 0.18;
      const scale = 0.78 + ((index * 17) % 9) * 0.035;
      this.crowdFigures.push({
        x,
        z: -11.75 - (index % 3) * 0.22,
        scale,
        phase: index * 0.61,
        baseRotation: (index % 2 ? -1 : 1) * 0.025,
      });
    }
    group.add(this.crowdBodyMesh, this.crowdHeadMesh);
    this.updateCrowd(0);

    const cable = this.createTube(
      [new THREE.Vector3(-14, 5.25, -11.55), new THREE.Vector3(0, 4.6, -11.8), new THREE.Vector3(14, 5.35, -11.6)],
      0.018,
      this.materials.darkMetal,
      46,
    );
    group.add(cable);
    const flagMaterials = [this.materials.redCloth, this.materials.creamCloth, this.materials.tealCloth];
    for (let index = 0; index < 22; index += 1) {
      const t = index / 21;
      const x = THREE.MathUtils.lerp(-13.4, 13.4, t);
      const y = 5.15 - Math.sin(t * Math.PI) * 0.55;
      this.buntingFlags.push({
        position: new THREE.Vector3(x, y, -11.45),
        material: flagMaterials[index % flagMaterials.length],
        width: 0.58,
        height: 0.82,
        baseRotation: (index % 2 ? -1 : 1) * 0.035,
        phase: index * 0.52,
      });
    }
    return group;
  }

  private createDistantBooth(
    x: number,
    z: number,
    scale: number,
    accent: THREE.MeshStandardMaterial,
  ): THREE.Group {
    const group = new THREE.Group();
    group.name = x < 0 ? 'distant-left-booth' : 'distant-right-booth';
    group.position.set(x, 0, z);
    group.scale.setScalar(scale);

    const wall = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(5.4, 3.4, 0.3)), this.materials.deepNavy);
    wall.position.y = 1.7;
    const counter = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(5.8, 0.72, 0.8)), accent);
    counter.position.set(0, 0.65, 0.4);
    const awningShape = new THREE.Shape();
    awningShape.moveTo(-3, 0);
    awningShape.lineTo(0, 1.6);
    awningShape.lineTo(3, 0);
    awningShape.closePath();
    const awning = new THREE.Mesh(this.geometry(new THREE.ShapeGeometry(awningShape)), this.materials.creamCloth);
    awning.position.set(0, 3.4, 0.18);
    const innerAwning = new THREE.Mesh(this.geometry(new THREE.ShapeGeometry(awningShape)), accent);
    innerAwning.scale.set(0.72, 0.72, 0.72);
    innerAwning.position.set(0, 3.43, 0.2);
    const sign = this.makeRoundedPanel(2.8, 0.55, 0.08, this.materials.gold, 0.12);
    sign.position.set(0, 2.72, 0.24);
    group.add(wall, counter, awning, innerAwning, sign);
    return group;
  }

  private createBooth(): THREE.Group {
    const booth = new THREE.Group();
    booth.name = 'hero-cup-toss-booth';
    booth.add(this.createBackWall());
    booth.add(this.createCounterAndSkirt());
    booth.add(this.createPrizeShelf());
    booth.add(this.createBoothFrame());
    booth.add(this.createCanopy());
    booth.add(this.createMarquee());
    booth.add(this.createMidgroundBunting());
    return booth;
  }

  private createBackWall(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'striped-booth-backdrop';
    const texture = this.canvasTexture(512, 512, (context, width, height) => {
      context.fillStyle = '#0c2c43';
      context.fillRect(0, 0, width, height);
      for (let x = 0; x < width; x += 64) {
        context.fillStyle = x % 128 === 0 ? '#123d54' : '#0e334b';
        context.fillRect(x, 0, 64, height);
        context.fillStyle = 'rgba(255, 239, 199, 0.055)';
        context.fillRect(x + 5, 0, 4, height);
      }
      context.fillStyle = 'rgba(255, 202, 79, 0.08)';
      for (let y = 48; y < height; y += 112) {
        for (let x = 38; x < width; x += 112) {
          this.drawCanvasStar(context, x + (y % 2) * 0.2, y, 14, 6, 5);
          context.fill();
        }
      }
    });
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2.2, 1.35);
    const wallMaterial = this.material(
      new THREE.MeshStandardMaterial({ map: texture, color: '#ffffff', roughness: 0.9, metalness: 0 }),
    );
    const wall = new THREE.Mesh(this.geometry(new THREE.PlaneGeometry(13.6, 8.75)), wallMaterial);
    wall.name = 'patterned-prize-wall';
    wall.position.set(0, 4.45, -9.42);
    wall.receiveShadow = true;
    group.add(wall);

    const frameHorizontal = this.geometry(new THREE.BoxGeometry(14.1, 0.18, 0.22));
    for (const y of [0.15, 8.75]) {
      const trim = new THREE.Mesh(frameHorizontal, this.materials.gold);
      trim.position.set(0, y, -9.26);
      group.add(trim);
    }
    const frameVertical = this.geometry(new THREE.BoxGeometry(0.18, 8.6, 0.22));
    for (const x of [-6.9, 6.9]) {
      const trim = new THREE.Mesh(frameVertical, this.materials.gold);
      trim.position.set(x, 4.45, -9.26);
      group.add(trim);
    }
    return group;
  }

  private createCounterAndSkirt(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'layered-counter-and-skirt';
    const apronTexture = this.canvasTexture(512, 256, (context, width, height) => {
      context.fillStyle = '#cf2948';
      context.fillRect(0, 0, width, height);
      for (let x = -height; x < width; x += 72) {
        context.fillStyle = '#e73b5b';
        context.beginPath();
        context.moveTo(x, 0);
        context.lineTo(x + 38, 0);
        context.lineTo(x + height + 38, height);
        context.lineTo(x + height, height);
        context.closePath();
        context.fill();
      }
      context.strokeStyle = 'rgba(95, 18, 49, 0.28)';
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(0, 18);
      context.lineTo(width, 18);
      context.moveTo(0, height - 18);
      context.lineTo(width, height - 18);
      context.stroke();
    });
    const apronMaterial = this.material(
      new THREE.MeshStandardMaterial({ color: '#ffffff', map: apronTexture, roughness: 0.88, metalness: 0 }),
    );
    const apron = this.makeRoundedPanel(11.25, 2.18, 0.22, apronMaterial, 0.18);
    apron.name = 'striped-front-apron';
    apron.position.set(0, 1.25, 1.71);
    apron.castShadow = true;
    apron.receiveShadow = true;
    group.add(apron);

    const topTrim = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(11.72, 0.22, 0.48)), this.materials.gold);
    topTrim.name = 'counter-front-gold-trim';
    topTrim.position.set(0, 2.38, 1.64);
    topTrim.castShadow = true;
    const lip = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(11.45, 0.13, 0.28)), this.materials.cream);
    lip.position.set(0, 2.52, 1.74);
    lip.castShadow = true;
    const lowerTrim = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(11.35, 0.16, 0.3)), this.materials.darkWood);
    lowerTrim.position.set(0, 0.16, 1.65);
    group.add(topTrim, lip, lowerTrim);

    const sideGeometry = this.geometry(new THREE.BoxGeometry(0.3, 2.18, 9.75));
    const sideTopGeometry = this.geometry(new THREE.BoxGeometry(0.4, 0.18, 9.84));
    for (const x of [-5.02, 5.02]) {
      const side = new THREE.Mesh(sideGeometry, this.materials.red);
      side.position.set(x, 1.25, -3.14);
      side.castShadow = true;
      side.receiveShadow = true;
      const sideTop = new THREE.Mesh(sideTopGeometry, this.materials.gold);
      sideTop.position.set(x, 2.38, -3.14);
      sideTop.castShadow = true;
      group.add(side, sideTop);
    }

    const panelGeometry = this.createRoundedPanelGeometry(3.18, 1.36, 0.055, 0.2);
    const panelMaterials = [this.materials.navy, this.materials.teal, this.materials.navy];
    for (let index = 0; index < 3; index += 1) {
      const panel = new THREE.Mesh(panelGeometry, panelMaterials[index]);
      panel.position.set(-3.7 + index * 3.7, 1.24, 1.855);
      group.add(panel);
      const star = new THREE.Mesh(this.createStarGeometry(0.39, 0.18, 5, 0.045), this.materials.gold);
      star.position.set(panel.position.x, panel.position.y, 1.925);
      star.rotation.z = index % 2 ? Math.PI / 5 : 0;
      group.add(star);
    }

    const pipingGeometry = this.geometry(new THREE.CylinderGeometry(0.035, 0.035, 1.75, 8));
    for (const x of [-5.2, -1.85, 1.85, 5.2]) {
      const piping = new THREE.Mesh(pipingGeometry, this.materials.cream);
      piping.position.set(x, 1.25, 1.95);
      group.add(piping);
    }

    const leftBrace = this.createTube(
      [new THREE.Vector3(-5.05, 0.3, 0.9), new THREE.Vector3(-5.05, 1.05, -0.2), new THREE.Vector3(-5.05, 2.3, -1.15)],
      0.075,
      this.materials.darkMetal,
      14,
    );
    const rightBrace = leftBrace.clone();
    rightBrace.position.x = 10.1;
    group.add(leftBrace, rightBrace);
    return group;
  }

  private createPrizeShelf(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'authored-prize-shelf';
    const shelfGeometry = this.geometry(new THREE.BoxGeometry(11.35, 0.18, 0.72));
    const shelfFrontGeometry = this.geometry(new THREE.BoxGeometry(11.5, 0.22, 0.16));
    for (const y of [3.76, 5.32]) {
      const shelf = new THREE.Mesh(shelfGeometry, this.materials.wood);
      shelf.position.set(0, y, -8.92);
      shelf.castShadow = true;
      const front = new THREE.Mesh(shelfFrontGeometry, this.materials.gold);
      front.position.set(0, y + 0.02, -8.53);
      group.add(shelf, front);
    }

    const dividers = this.geometry(new THREE.BoxGeometry(0.12, 1.42, 0.28));
    for (const x of [-5.6, -2.8, 0, 2.8, 5.6]) {
      const divider = new THREE.Mesh(dividers, this.materials.cream);
      divider.position.set(x, 4.52, -9.13);
      group.add(divider);
    }

    this.placePrize(group, this.createTeddyPrize(this.materials.pink), -4.3, 3.87, -8.48, 0.84, 0.1, 0.025);
    this.placePrize(group, this.createDuckPrize(), -2.18, 3.88, -8.48, 0.88, 0.82, 0.018);
    this.placePrize(group, this.createRocketPrize(this.materials.skyBlue), 0, 3.86, -8.47, 0.82, 1.55, 0.045);
    this.placePrize(group, this.createDrumPrize(), 2.24, 3.88, -8.49, 0.9, 2.2, -0.02);
    this.placePrize(group, this.createStarPrize(), 4.35, 3.84, -8.5, 0.78, 2.9, 0.035);

    this.placePrize(group, this.createTrainPrize(), -3.82, 5.43, -8.48, 0.76, 0.5, 0.018);
    this.placePrize(group, this.createBalloonPrize(), -1.28, 5.38, -8.5, 0.7, 1.35, -0.02);
    this.placePrize(group, this.createTeddyPrize(this.materials.mint), 1.28, 5.43, -8.49, 0.72, 2.1, 0.02);
    this.placePrize(group, this.createRocketPrize(this.materials.orange), 3.82, 5.39, -8.49, 0.7, 2.7, -0.04);
    return group;
  }

  private placePrize(
    parent: THREE.Group,
    prize: THREE.Group,
    x: number,
    y: number,
    z: number,
    scale: number,
    phase: number,
    spin: number,
  ): void {
    prize.position.set(x, y, z);
    prize.scale.setScalar(scale);
    parent.add(prize);
    this.animatedProps.push({ root: prize, baseY: y, baseRotationY: 0, phase, bob: 0.025, spin });
  }

  private createTeddyPrize(bodyMaterial: THREE.MeshStandardMaterial): THREE.Group {
    const root = new THREE.Group();
    root.name = 'teddy-prize';
    const body = new THREE.Mesh(this.geometry(new THREE.CapsuleGeometry(0.28, 0.42, 5, 10)), bodyMaterial);
    body.position.y = 0.52;
    const belly = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.2, 12, 8)), this.materials.cream);
    belly.scale.set(1, 1.25, 0.33);
    belly.position.set(0, 0.54, 0.25);
    const head = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.35, 14, 10)), bodyMaterial);
    head.position.y = 1.08;
    const muzzle = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.16, 10, 7)), this.materials.cream);
    muzzle.scale.z = 0.6;
    muzzle.position.set(0, 1.01, 0.3);
    const earGeometry = this.geometry(new THREE.SphereGeometry(0.14, 10, 7));
    for (const x of [-0.27, 0.27]) {
      const ear = new THREE.Mesh(earGeometry, bodyMaterial);
      ear.position.set(x, 1.3, 0);
      root.add(ear);
    }
    const armGeometry = this.geometry(new THREE.CapsuleGeometry(0.09, 0.28, 4, 8));
    for (const x of [-0.34, 0.34]) {
      const arm = new THREE.Mesh(armGeometry, bodyMaterial);
      arm.position.set(x, 0.62, 0);
      arm.rotation.z = x < 0 ? -0.55 : 0.55;
      root.add(arm);
    }
    const eyeGeometry = this.geometry(new THREE.SphereGeometry(0.035, 7, 5));
    for (const x of [-0.11, 0.11]) {
      const eye = new THREE.Mesh(eyeGeometry, this.materials.ink);
      eye.position.set(x, 1.13, 0.32);
      root.add(eye);
    }
    const bowGeometry = this.createBowGeometry();
    const bow = new THREE.Mesh(bowGeometry, this.materials.gold);
    bow.position.set(0, 0.83, 0.31);
    root.add(body, belly, head, muzzle, bow);
    return root;
  }

  private createDuckPrize(): THREE.Group {
    const root = new THREE.Group();
    root.name = 'duck-prize';
    const body = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.42, 14, 9)), this.materials.gold);
    body.scale.set(1.2, 0.82, 1.35);
    body.position.y = 0.42;
    const head = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.28, 12, 9)), this.materials.gold);
    head.position.set(0, 0.86, 0.08);
    const bill = new THREE.Mesh(this.geometry(new THREE.ConeGeometry(0.16, 0.36, 4)), this.materials.orange);
    bill.rotation.x = Math.PI / 2;
    bill.rotation.z = Math.PI / 4;
    bill.scale.y = 0.72;
    bill.position.set(0, 0.8, 0.33);
    const wingGeometry = this.geometry(new THREE.SphereGeometry(0.24, 10, 7));
    for (const x of [-0.38, 0.38]) {
      const wing = new THREE.Mesh(wingGeometry, this.materials.orange);
      wing.scale.set(0.45, 0.9, 1.1);
      wing.position.set(x, 0.45, 0.02);
      root.add(wing);
    }
    const eyeGeometry = this.geometry(new THREE.SphereGeometry(0.035, 7, 5));
    for (const x of [-0.09, 0.09]) {
      const eye = new THREE.Mesh(eyeGeometry, this.materials.ink);
      eye.position.set(x, 0.91, 0.26);
      root.add(eye);
    }
    root.add(body, head, bill);
    return root;
  }

  private createRocketPrize(shellMaterial: THREE.MeshStandardMaterial): THREE.Group {
    const root = new THREE.Group();
    root.name = 'rocket-prize';
    const profile = [
      new THREE.Vector2(0.19, 0),
      new THREE.Vector2(0.27, 0.1),
      new THREE.Vector2(0.3, 0.58),
      new THREE.Vector2(0.25, 0.82),
      new THREE.Vector2(0.08, 1.14),
      new THREE.Vector2(0, 1.25),
    ];
    const hull = new THREE.Mesh(this.geometry(new THREE.LatheGeometry(profile, 16)), shellMaterial);
    hull.position.y = 0.12;
    const windowFrame = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.145, 0.145, 0.055, 16)), this.materials.gold);
    windowFrame.rotation.x = Math.PI / 2;
    windowFrame.position.set(0, 0.82, 0.275);
    const windowGlass = new THREE.Mesh(this.geometry(new THREE.CircleGeometry(0.1, 16)), this.materials.deepNavy);
    windowGlass.position.set(0, 0.82, 0.307);
    const finGeometry = this.createFinGeometry();
    for (const x of [-0.29, 0.29]) {
      const fin = new THREE.Mesh(finGeometry, this.materials.red);
      fin.position.set(x, 0.2, 0);
      if (x > 0) fin.scale.x = -1;
      root.add(fin);
    }
    const nozzle = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.18, 0.24, 0.18, 12)), this.materials.darkMetal);
    nozzle.position.y = 0.03;
    root.add(hull, windowFrame, windowGlass, nozzle);
    return root;
  }

  private createDrumPrize(): THREE.Group {
    const root = new THREE.Group();
    root.name = 'drum-prize';
    const shell = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.43, 0.43, 0.58, 20)), this.materials.red);
    shell.position.y = 0.35;
    const headGeometry = this.geometry(new THREE.CylinderGeometry(0.45, 0.45, 0.055, 20));
    for (const y of [0.05, 0.65]) {
      const head = new THREE.Mesh(headGeometry, this.materials.cream);
      head.position.y = y;
      root.add(head);
    }
    const ringGeometry = this.geometry(new THREE.TorusGeometry(0.43, 0.035, 6, 20));
    for (const y of [0.08, 0.62]) {
      const ring = new THREE.Mesh(ringGeometry, this.materials.gold);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = y;
      root.add(ring);
    }
    const braceGeometry = this.geometry(new THREE.CylinderGeometry(0.018, 0.018, 0.67, 6));
    for (let index = 0; index < 6; index += 1) {
      const angle = (index / 6) * Math.PI * 2;
      const brace = new THREE.Mesh(braceGeometry, this.materials.gold);
      brace.position.set(Math.cos(angle) * 0.425, 0.35, Math.sin(angle) * 0.425);
      brace.rotation.z = index % 2 ? 0.1 : -0.1;
      root.add(brace);
    }
    const stickGeometry = this.geometry(new THREE.CylinderGeometry(0.018, 0.022, 0.75, 7));
    for (const x of [-0.13, 0.13]) {
      const stick = new THREE.Mesh(stickGeometry, this.materials.wood);
      stick.position.set(x, 0.92, 0.02);
      stick.rotation.z = x < 0 ? -0.34 : 0.34;
      root.add(stick);
    }
    root.add(shell);
    return root;
  }

  private createStarPrize(): THREE.Group {
    const root = new THREE.Group();
    root.name = 'star-ribbon-prize';
    const star = new THREE.Mesh(this.createStarGeometry(0.58, 0.27, 5, 0.14), this.materials.gold);
    star.position.set(0, 0.76, 0);
    star.rotation.z = 0.04;
    const inner = new THREE.Mesh(this.createStarGeometry(0.3, 0.14, 5, 0.045), this.materials.pink);
    inner.position.set(0, 0.76, 0.1);
    inner.rotation.z = 0.04;
    const ribbonShape = new THREE.Shape();
    ribbonShape.moveTo(-0.34, 0.54);
    ribbonShape.lineTo(-0.07, 0.54);
    ribbonShape.lineTo(-0.16, 0);
    ribbonShape.lineTo(-0.31, 0.14);
    ribbonShape.closePath();
    const ribbonGeometry = this.geometry(new THREE.ShapeGeometry(ribbonShape));
    const leftRibbon = new THREE.Mesh(ribbonGeometry, this.materials.tealCloth);
    const rightRibbon = leftRibbon.clone();
    rightRibbon.scale.x = -1;
    root.add(leftRibbon, rightRibbon, star, inner);
    return root;
  }

  private createTrainPrize(): THREE.Group {
    const root = new THREE.Group();
    root.name = 'toy-train-prize';
    const base = this.makeRoundedPanel(1.32, 0.22, 0.38, this.materials.gold, 0.08);
    base.rotation.x = Math.PI / 2;
    base.position.set(0, 0.25, 0);
    const boiler = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.24, 0.24, 0.72, 14)), this.materials.teal);
    boiler.rotation.z = Math.PI / 2;
    boiler.position.set(0.14, 0.52, 0);
    const cab = this.makeRoundedPanel(0.5, 0.62, 0.5, this.materials.red, 0.08);
    cab.position.set(-0.42, 0.62, 0);
    const roof = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(0.67, 0.1, 0.62)), this.materials.cream);
    roof.position.set(-0.42, 0.98, 0);
    const stack = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.11, 0.17, 0.4, 12)), this.materials.darkMetal);
    stack.position.set(0.37, 0.9, 0);
    const wheelGeometry = this.geometry(new THREE.CylinderGeometry(0.17, 0.17, 0.11, 12));
    for (const x of [-0.46, 0.08, 0.46]) {
      const wheel = new THREE.Mesh(wheelGeometry, this.materials.purple);
      wheel.rotation.x = Math.PI / 2;
      wheel.position.set(x, 0.2, 0.26);
      root.add(wheel);
    }
    root.add(base, boiler, cab, roof, stack);
    return root;
  }

  private createBalloonPrize(): THREE.Group {
    const root = new THREE.Group();
    root.name = 'balloon-bunch-prize';
    const colors = [this.materials.red, this.materials.gold, this.materials.teal, this.materials.purple];
    const positions = [
      [-0.27, 0.9, 0],
      [0.25, 0.98, -0.04],
      [0, 1.25, 0.02],
      [0.05, 0.72, 0.12],
    ];
    const balloonGeometry = this.geometry(new THREE.SphereGeometry(0.25, 12, 9));
    positions.forEach(([x, y, z], index) => {
      const balloon = new THREE.Mesh(balloonGeometry, colors[index]);
      balloon.scale.set(0.9, 1.2, 0.9);
      balloon.position.set(x, y, z);
      const string = this.createTube(
        [new THREE.Vector3(x, y - 0.28, z), new THREE.Vector3(x * 0.55, 0.48, z), new THREE.Vector3(0, 0.03, 0)],
        0.009,
        this.materials.cream,
        8,
      );
      root.add(balloon, string);
    });
    const knot = new THREE.Mesh(this.createBowGeometry(), this.materials.pink);
    knot.scale.setScalar(0.65);
    knot.position.y = 0.08;
    root.add(knot);
    return root;
  }

  private createBoothFrame(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'booth-posts-and-trim';
    for (const [x, z, front] of [
      [-6.18, 1.22, true],
      [6.18, 1.22, true],
      [-6.18, -9.12, false],
      [6.18, -9.12, false],
    ] as const) {
      group.add(this.createDecorativePost(x, z, front));
    }
    const upperSideGeometry = this.geometry(new THREE.BoxGeometry(0.2, 0.2, 10.35));
    for (const x of [-6.18, 6.18]) {
      const rail = new THREE.Mesh(upperSideGeometry, this.materials.gold);
      rail.position.set(x, 7.72, -3.95);
      group.add(rail);
    }
    return group;
  }

  private createDecorativePost(x: number, z: number, front: boolean): THREE.Group {
    const root = new THREE.Group();
    root.name = `${front ? 'front' : 'rear'}-${x < 0 ? 'left' : 'right'}-post`;
    const shaft = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.23, 0.29, 7.45, 8)), this.materials.red);
    shaft.position.y = 3.78;
    shaft.castShadow = front;
    const inset = new THREE.Mesh(this.geometry(new THREE.CylinderGeometry(0.105, 0.12, 7.18, 8)), this.materials.cream);
    inset.position.set(0, 3.8, 0.22);
    const bandGeometry = this.geometry(new THREE.TorusGeometry(0.3, 0.045, 6, 12));
    for (const y of [0.44, 2.4, 4.42, 6.55, 7.46]) {
      const band = new THREE.Mesh(bandGeometry, this.materials.gold);
      band.rotation.x = Math.PI / 2;
      band.position.y = y;
      root.add(band);
    }
    const cap = new THREE.Mesh(this.geometry(new THREE.SphereGeometry(0.35, 12, 8)), this.materials.gold);
    cap.scale.y = 0.65;
    cap.position.y = 7.62;
    const finial = new THREE.Mesh(this.geometry(new THREE.ConeGeometry(0.18, 0.48, 8)), this.materials.pink);
    finial.position.y = 7.95;
    root.position.set(x, 0, z);
    root.add(shaft, inset, cap, finial);
    if (front) {
      this.addBulbString(
        [
          new THREE.Vector3(x, 0.7, z + 0.3),
          new THREE.Vector3(x, 3.9, z + 0.3),
          new THREE.Vector3(x, 7.25, z + 0.3),
        ],
        9,
      );
    }
    return root;
  }

  private createCanopy(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'striped-canopy-and-valance';
    const canopyTexture = this.canvasTexture(768, 512, (context, width, height) => {
      const stripes = 12;
      const stripeWidth = width / stripes;
      for (let index = 0; index < stripes; index += 1) {
        context.fillStyle = index % 2 ? '#f8dfae' : '#d92f50';
        context.fillRect(index * stripeWidth, 0, stripeWidth + 1, height);
        context.fillStyle = index % 2 ? 'rgba(255,255,255,0.12)' : 'rgba(96,12,42,0.13)';
        context.fillRect(index * stripeWidth + stripeWidth * 0.1, 0, stripeWidth * 0.1, height);
      }
      const shade = context.createLinearGradient(0, 0, 0, height);
      shade.addColorStop(0, 'rgba(255,255,255,0.18)');
      shade.addColorStop(0.55, 'rgba(255,255,255,0)');
      shade.addColorStop(1, 'rgba(24, 11, 35, 0.25)');
      context.fillStyle = shade;
      context.fillRect(0, 0, width, height);
    });
    const canopyMaterial = this.material(
      new THREE.MeshStandardMaterial({ map: canopyTexture, color: '#ffffff', roughness: 0.86, side: THREE.DoubleSide }),
    );
    const roofGeometry = this.geometry(new THREE.BufferGeometry());
    roofGeometry.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        [-6.95, 7.83, 1.55, 6.95, 7.83, 1.55, -6.95, 9.42, -9.72, 6.95, 9.42, -9.72],
        3,
      ),
    );
    roofGeometry.setAttribute('uv', new THREE.Float32BufferAttribute([0, 0, 1, 0, 0, 1, 1, 1], 2));
    roofGeometry.setIndex([0, 1, 2, 2, 1, 3]);
    roofGeometry.computeVertexNormals();
    const roof = new THREE.Mesh(roofGeometry, canopyMaterial);
    roof.name = 'sloped-striped-canopy';
    roof.receiveShadow = true;
    group.add(roof);

    const frontBeam = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(14.2, 0.25, 0.34)), this.materials.gold);
    frontBeam.position.set(0, 7.85, 1.47);
    frontBeam.castShadow = true;
    group.add(frontBeam);

    const scallopGeometry = this.createScallopGeometry(1.15, 0.58, 0.09);
    const valanceMaterials = [this.materials.redCloth, this.materials.creamCloth];
    for (let index = 0; index < 12; index += 1) {
      const scallop = new THREE.Mesh(scallopGeometry, valanceMaterials[index % 2]);
      scallop.position.set(-6.325 + index * 1.15, 7.76, 1.49);
      group.add(scallop);
    }
    this.addBulbString(
      [new THREE.Vector3(-6.45, 7.78, 1.72), new THREE.Vector3(0, 7.66, 1.74), new THREE.Vector3(6.45, 7.78, 1.72)],
      21,
    );
    return group;
  }

  private createMarquee(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'cup-toss-marquee';
    group.position.set(0, 7.03, -9.08);
    const outer = this.makeRoundedPanel(10.1, 1.9, 0.22, this.materials.marqueeGlow, 0.34);
    const inner = this.makeRoundedPanel(9.54, 1.46, 0.12, this.materials.red, 0.25);
    inner.position.z = 0.15;
    const titleTexture = this.canvasTexture(1024, 256, (context, width, height) => {
      const gradient = context.createLinearGradient(0, 0, width, 0);
      gradient.addColorStop(0, '#0c4761');
      gradient.addColorStop(0.5, '#173650');
      gradient.addColorStop(1, '#0c4761');
      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);
      context.strokeStyle = 'rgba(255, 211, 98, 0.35)';
      context.lineWidth = 8;
      context.strokeRect(13, 13, width - 26, height - 26);
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.font = '900 78px Arial Black, Impact, sans-serif';
      context.lineJoin = 'round';
      context.strokeStyle = '#8a163f';
      context.lineWidth = 20;
      context.strokeText('CARNIVAL CUP TOSS', width / 2, 112);
      context.fillStyle = '#fff0cb';
      context.fillText('CARNIVAL CUP TOSS', width / 2, 112);
      context.font = '700 32px Arial, sans-serif';
      context.letterSpacing = '7px';
      context.fillStyle = '#ffd362';
      context.fillText('SKILL  •  BOUNCE  •  WIN', width / 2, 201);
    });
    const titleMaterial = this.material(new THREE.MeshBasicMaterial({ map: titleTexture, side: THREE.DoubleSide, toneMapped: false }));
    const title = new THREE.Mesh(this.geometry(new THREE.PlaneGeometry(9.16, 1.1)), titleMaterial);
    title.position.z = 0.34;
    title.renderOrder = 3;
    group.add(outer, inner, title);

    const leftStar = new THREE.Mesh(this.createStarGeometry(0.55, 0.24, 8, 0.08), this.materials.cream);
    leftStar.position.set(-5.05, 0, 0.12);
    const rightStar = leftStar.clone();
    rightStar.position.x = 5.05;
    rightStar.rotation.z = Math.PI / 8;
    group.add(leftStar, rightStar);

    const signZ = -8.78;
    this.addBulbString(
      [new THREE.Vector3(-4.65, 7.92, signZ), new THREE.Vector3(0, 7.98, signZ), new THREE.Vector3(4.65, 7.92, signZ)],
      15,
    );
    this.addBulbString(
      [new THREE.Vector3(-4.65, 6.16, signZ), new THREE.Vector3(0, 6.1, signZ), new THREE.Vector3(4.65, 6.16, signZ)],
      15,
    );
    return group;
  }

  private createMidgroundBunting(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'midground-bunting';
    const start = new THREE.Vector3(-5.85, 6.08, -8.68);
    const middle = new THREE.Vector3(0, 5.45, -8.58);
    const end = new THREE.Vector3(5.85, 6.08, -8.68);
    group.add(this.createTube([start, middle, end], 0.014, this.materials.darkMetal, 36));

    const flagMaterials = [this.materials.gold, this.materials.redCloth, this.materials.tealCloth, this.materials.creamCloth];
    for (let index = 0; index < 15; index += 1) {
      const t = index / 14;
      const x = THREE.MathUtils.lerp(start.x, end.x, t);
      const y = 6.02 - Math.sin(t * Math.PI) * 0.6;
      this.buntingFlags.push({
        position: new THREE.Vector3(x, y, -8.47),
        material: flagMaterials[index % flagMaterials.length],
        width: 0.63,
        height: 0.82,
        baseRotation: (index % 2 ? -1 : 1) * 0.025,
        phase: index * 0.77 + 1.2,
      });
    }
    return group;
  }

  private createForegroundAccents(): THREE.Group {
    const group = new THREE.Group();
    group.name = 'foreground-ticket-and-rail-accents';
    const postProfile = [
      new THREE.Vector2(0.22, 0),
      new THREE.Vector2(0.28, 0.08),
      new THREE.Vector2(0.15, 0.18),
      new THREE.Vector2(0.11, 0.88),
      new THREE.Vector2(0.19, 1.05),
      new THREE.Vector2(0.08, 1.19),
      new THREE.Vector2(0, 1.24),
    ];
    const postGeometry = this.geometry(new THREE.LatheGeometry(postProfile, 14));
    for (const x of [-5.85, 5.85]) {
      const post = new THREE.Mesh(postGeometry, this.materials.gold);
      post.name = x < 0 ? 'left-ticket-rail-post' : 'right-ticket-rail-post';
      post.position.set(x, 0, 5.7);
      post.castShadow = true;
      group.add(post);

      const ticketStack = this.createTicketStack(x < 0 ? this.materials.pink : this.materials.teal);
      ticketStack.position.set(x + (x < 0 ? -0.52 : 0.52), 0.09, 5.65);
      ticketStack.rotation.y = x < 0 ? 0.18 : -0.18;
      group.add(ticketStack);
    }
    const rope = this.createTube(
      [new THREE.Vector3(-5.78, 1.02, 5.7), new THREE.Vector3(0, 0.58, 5.82), new THREE.Vector3(5.78, 1.02, 5.7)],
      0.055,
      this.materials.red,
      52,
    );
    rope.name = 'velvet-ticket-rail';
    group.add(rope);

    for (const x of [-6.9, 6.9]) {
      const directional = this.makeRoundedPanel(1.55, 0.64, 0.1, this.materials.navy, 0.14);
      directional.position.set(x, 1.85, 3.7);
      directional.rotation.y = x < 0 ? 0.2 : -0.2;
      const arrowShape = new THREE.Shape();
      arrowShape.moveTo(-0.42, -0.12);
      arrowShape.lineTo(0.12, -0.12);
      arrowShape.lineTo(0.12, -0.28);
      arrowShape.lineTo(0.48, 0);
      arrowShape.lineTo(0.12, 0.28);
      arrowShape.lineTo(0.12, 0.12);
      arrowShape.lineTo(-0.42, 0.12);
      arrowShape.closePath();
      const arrow = new THREE.Mesh(this.geometry(new THREE.ShapeGeometry(arrowShape)), this.materials.gold);
      arrow.position.set(x, 1.85, 3.77);
      arrow.scale.x = x < 0 ? 1 : -1;
      group.add(directional, arrow);
    }
    return group;
  }

  private createTicketStack(accent: THREE.MeshStandardMaterial): THREE.Group {
    const root = new THREE.Group();
    root.name = 'ticket-stack';
    const ticketGeometry = this.createTicketGeometry(0.92, 0.42, 0.035);
    for (let index = 0; index < 5; index += 1) {
      const ticket = new THREE.Mesh(ticketGeometry, index % 2 ? this.materials.cream : accent);
      ticket.position.set(index * 0.025, index * 0.035, -index * 0.018);
      ticket.rotation.z = (index - 2) * 0.035;
      root.add(ticket);
    }
    const stripe = new THREE.Mesh(this.geometry(new THREE.BoxGeometry(0.34, 0.055, 0.045)), this.materials.gold);
    stripe.position.set(0.18, 0.2, 0.08);
    root.add(stripe);
    return root;
  }

  private finishBuntingInstances(): void {
    const unitGeometry = this.createPennantGeometry(1, 1);
    const byMaterial = new Map<THREE.Material, BuntingFlag[]>();
    for (const flag of this.buntingFlags) {
      const flags = byMaterial.get(flag.material) ?? [];
      flags.push(flag);
      byMaterial.set(flag.material, flags);
    }

    for (const [material, flags] of byMaterial) {
      const mesh = new THREE.InstancedMesh(unitGeometry, material, flags.length);
      mesh.name = `instanced-bunting-${this.buntingMeshes.length + 1}`;
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      flags.forEach((flag, instanceIndex) => {
        flag.mesh = mesh;
        flag.instanceIndex = instanceIndex;
      });
      this.buntingMeshes.push(mesh);
      this.group.add(mesh);
    }

    this.updateBunting(0);
    for (const mesh of this.buntingMeshes) {
      mesh.computeBoundingBox();
      mesh.computeBoundingSphere();
    }
  }

  private batchStaticEnvironment(): void {
    this.group.updateMatrixWorld(true);
    const inverseRoot = new THREE.Matrix4().copy(this.group.matrixWorld).invert();
    const animatedRoots = new Set<THREE.Object3D>(this.animatedProps.map((prop) => prop.root));
    const buckets = new Map<
      string,
      {
        material: THREE.Material;
        castShadow: boolean;
        receiveShadow: boolean;
        renderOrder: number;
        layers: number;
        meshes: THREE.Mesh[];
      }
    >();

    const belongsToAnimatedRoot = (object: THREE.Object3D): boolean => {
      let cursor: THREE.Object3D | null = object;
      while (cursor && cursor !== this.group) {
        if (animatedRoots.has(cursor)) return true;
        cursor = cursor.parent;
      }
      return false;
    };

    this.group.traverse((object) => {
      if (!(object instanceof THREE.Mesh) || object instanceof THREE.InstancedMesh) return;
      if (belongsToAnimatedRoot(object) || Array.isArray(object.material)) return;
      if (!object.geometry.getAttribute('position') || object.matrixWorld.determinant() < 0) return;
      const geometryAttributes = object.geometry.attributes as Record<
        string,
        THREE.BufferAttribute | THREE.InterleavedBufferAttribute
      >;
      const attributes = Object.entries(geometryAttributes)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([name, attribute]) => {
          return `${name}:${attribute.itemSize}:${attribute.normalized ? 1 : 0}:${attribute instanceof THREE.InterleavedBufferAttribute ? 'i' : 'b'}`;
        })
        .join('|');
      const key = [
        object.material.uuid,
        object.castShadow ? 1 : 0,
        object.receiveShadow ? 1 : 0,
        object.renderOrder,
        object.layers.mask,
        attributes,
      ].join(':');
      const bucket = buckets.get(key) ?? {
        material: object.material,
        castShadow: object.castShadow,
        receiveShadow: object.receiveShadow,
        renderOrder: object.renderOrder,
        layers: object.layers.mask,
        meshes: [] as THREE.Mesh[],
      };
      bucket.meshes.push(object);
      buckets.set(key, bucket);
    });

    const removed = new Set<THREE.Mesh>();
    let batchIndex = 0;
    for (const bucket of buckets.values()) {
      if (bucket.meshes.length < 2) continue;
      const transformed: THREE.BufferGeometry[] = [];
      for (const mesh of bucket.meshes) {
        const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
        const localMatrix = new THREE.Matrix4().multiplyMatrices(inverseRoot, mesh.matrixWorld);
        geometry.applyMatrix4(localMatrix);
        transformed.push(geometry);
      }

      const merged = mergeGeometries(transformed, false);
      for (const geometry of transformed) geometry.dispose();
      if (!merged) continue;

      this.geometry(merged);
      merged.computeBoundingBox();
      merged.computeBoundingSphere();
      const batch = new THREE.Mesh(merged, bucket.material);
      batchIndex += 1;
      batch.name = `static-art-batch-${batchIndex}`;
      batch.castShadow = bucket.castShadow;
      batch.receiveShadow = bucket.receiveShadow;
      batch.renderOrder = bucket.renderOrder;
      batch.layers.mask = bucket.layers;
      batch.matrixAutoUpdate = false;
      batch.updateMatrix();
      this.group.add(batch);
      for (const mesh of bucket.meshes) removed.add(mesh);
    }

    for (const mesh of removed) mesh.removeFromParent();

    const liveGeometries = new Set<THREE.BufferGeometry>();
    this.group.traverse((object) => {
      if (object instanceof THREE.Mesh) liveGeometries.add(object.geometry);
    });
    for (const mesh of removed) {
      if (liveGeometries.has(mesh.geometry) || !this.ownedGeometries.has(mesh.geometry)) continue;
      mesh.geometry.dispose();
      this.ownedGeometries.delete(mesh.geometry);
    }
  }

  private finishBulbInstances(): void {
    const geometry = this.geometry(new THREE.SphereGeometry(0.072, 8, 6));
    const material = this.material(
      new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.96, toneMapped: false }),
    );
    this.bulbMesh = new THREE.InstancedMesh(geometry, material, this.bulbPoints.length);
    this.bulbMesh.name = 'instanced-warm-marquee-bulbs';
    this.bulbMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    const palette = ['#ffd570', '#ff7b73', '#6ff1d1', '#fff2cf'];
    for (let index = 0; index < this.bulbPoints.length; index += 1) {
      this.dummy.position.copy(this.bulbPoints[index].position);
      this.dummy.scale.setScalar(1);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.updateMatrix();
      this.bulbMesh.setMatrixAt(index, this.dummy.matrix);
      this.bulbMesh.setColorAt(index, new THREE.Color(palette[index % palette.length]));
    }
    this.bulbMesh.instanceMatrix.needsUpdate = true;
    if (this.bulbMesh.instanceColor) this.bulbMesh.instanceColor.needsUpdate = true;
    this.group.add(this.bulbMesh);
  }

  private addBulbString(points: THREE.Vector3[], bulbCount: number): void {
    const cable = this.createTube(points, 0.018, this.materials.darkMetal, Math.max(8, bulbCount * 2));
    cable.name = 'bulb-string-cable';
    this.group.add(cable);
    const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal');
    for (let index = 0; index < bulbCount; index += 1) {
      const t = bulbCount === 1 ? 0 : index / (bulbCount - 1);
      this.bulbPoints.push({ position: curve.getPoint(t), phase: this.bulbPoints.length * 0.57 });
    }
  }

  private buildVfxPools(): void {
    const confettiGeometry = this.geometry(new THREE.BoxGeometry(0.085, 0.018, 0.19));
    const confettiMaterial = this.material(
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.98,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    this.confettiMesh = new THREE.InstancedMesh(confettiGeometry, confettiMaterial, CONFETTI_CAPACITY);
    this.confettiMesh.name = 'pooled-confetti-burst-vfx';
    this.confettiMesh.frustumCulled = false;
    this.confettiMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let index = 0; index < CONFETTI_CAPACITY; index += 1) {
      this.confettiStates.push({
        active: false,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        rotation: new THREE.Euler(),
        spin: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
      });
      this.writeHiddenInstance(this.confettiMesh, index);
      this.confettiMesh.setColorAt(index, new THREE.Color(CONFETTI_COLORS[index % CONFETTI_COLORS.length]));
    }
    this.confettiMesh.instanceMatrix.needsUpdate = true;

    const puffGeometry = this.geometry(new THREE.IcosahedronGeometry(0.5, 0));
    const puffMaterial = this.material(
      new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.58, depthWrite: false, toneMapped: false }),
    );
    this.puffMesh = new THREE.InstancedMesh(puffGeometry, puffMaterial, PUFF_CAPACITY);
    this.puffMesh.name = 'pooled-impact-puffs';
    this.puffMesh.frustumCulled = false;
    this.puffMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let index = 0; index < PUFF_CAPACITY; index += 1) {
      this.puffStates.push({
        active: false,
        position: new THREE.Vector3(),
        velocity: new THREE.Vector3(),
        life: 0,
        maxLife: 1,
        size: 0.1,
      });
      this.writeHiddenInstance(this.puffMesh, index);
      this.puffMesh.setColorAt(index, new THREE.Color(index % 3 ? '#fff2cf' : '#ffcf58'));
    }
    this.puffMesh.instanceMatrix.needsUpdate = true;

    const ringGeometry = this.geometry(new THREE.RingGeometry(0.22, 0.29, 32));
    for (let index = 0; index < RING_CAPACITY; index += 1) {
      const material = this.material(
        new THREE.MeshBasicMaterial({
          color: '#fff2cf',
          transparent: true,
          opacity: 0,
          side: THREE.DoubleSide,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      const mesh = new THREE.Mesh(ringGeometry, material);
      mesh.name = `impact-ring-${index + 1}`;
      mesh.rotation.x = -Math.PI / 2;
      mesh.visible = false;
      this.ringStates.push({ mesh, active: false, life: 0, maxLife: 1, size: 1 });
      this.group.add(mesh);
    }

    this.buildAimIndicator();
    this.group.add(this.confettiMesh, this.puffMesh, this.aimGroup);
  }

  private buildAimIndicator(): void {
    this.aimGroup.name = 'responsive-aim-pulse';
    this.aimGroup.visible = false;
    const rings = [
      { radius: 0.42, tube: 0.028, opacity: 0.9 },
      { radius: 0.7, tube: 0.018, opacity: 0.62 },
    ];
    rings.forEach(({ radius, tube, opacity }, index) => {
      const material = this.material(
        new THREE.MeshBasicMaterial({
          color: index === 0 ? '#ffd362' : '#fff0cb',
          transparent: true,
          opacity,
          depthWrite: false,
          blending: THREE.AdditiveBlending,
        }),
      );
      this.aimMaterials.push(material);
      const ring = new THREE.Mesh(this.geometry(new THREE.TorusGeometry(radius, tube, 7, 40)), material);
      ring.rotation.x = Math.PI / 2;
      ring.name = index === 0 ? 'aim-inner-ring' : 'aim-outer-ring';
      this.aimGroup.add(ring);
    });

    const chevronShape = new THREE.Shape();
    chevronShape.moveTo(-0.12, 0);
    chevronShape.lineTo(0.12, 0);
    chevronShape.lineTo(0, -0.28);
    chevronShape.closePath();
    const chevronGeometry = this.geometry(new THREE.ShapeGeometry(chevronShape));
    const chevronMaterial = this.material(
      new THREE.MeshBasicMaterial({ color: '#ffcf58', transparent: true, opacity: 0.82, depthWrite: false, side: THREE.DoubleSide }),
    );
    this.aimMaterials.push(chevronMaterial);
    for (let index = 0; index < 4; index += 1) {
      const chevron = new THREE.Mesh(chevronGeometry, chevronMaterial);
      chevron.rotation.x = -Math.PI / 2;
      chevron.rotation.z = index * (Math.PI / 2);
      chevron.position.set(Math.sin(index * Math.PI / 2) * 0.83, 0, Math.cos(index * Math.PI / 2) * 0.83);
      this.aimGroup.add(chevron);
    }
  }

  private updateBulbs(elapsed: number): void {
    if (!this.bulbMesh) return;
    for (let index = 0; index < this.bulbPoints.length; index += 1) {
      const bulb = this.bulbPoints[index];
      const pulse = 0.9 + Math.sin(elapsed * (this.roundOver ? 7.8 : 2.6) + bulb.phase) * (this.roundOver ? 0.17 : 0.07);
      this.dummy.position.copy(bulb.position);
      this.dummy.rotation.set(0, 0, 0);
      this.dummy.scale.setScalar(pulse);
      this.dummy.updateMatrix();
      this.bulbMesh.setMatrixAt(index, this.dummy.matrix);
    }
    this.bulbMesh.instanceMatrix.needsUpdate = true;
  }

  private updateAnimatedProps(elapsed: number): void {
    for (const prop of this.animatedProps) {
      prop.root.position.y = prop.baseY + Math.sin(elapsed * 1.45 + prop.phase) * prop.bob;
      prop.root.rotation.y = prop.baseRotationY + Math.sin(elapsed * 0.78 + prop.phase) * 0.025 + elapsed * prop.spin;
    }
  }

  private updateBunting(elapsed: number): void {
    for (const flag of this.buntingFlags) {
      if (!flag.mesh || flag.instanceIndex === undefined) continue;
      this.dummy.position.copy(flag.position);
      this.dummy.rotation.set(0, 0, flag.baseRotation + Math.sin(elapsed * 2.2 + flag.phase) * 0.035);
      this.dummy.scale.set(flag.width * (1 + Math.sin(elapsed * 2.7 + flag.phase) * 0.025), flag.height, 1);
      this.dummy.updateMatrix();
      flag.mesh.setMatrixAt(flag.instanceIndex, this.dummy.matrix);
    }
    for (const mesh of this.buntingMeshes) mesh.instanceMatrix.needsUpdate = true;
  }

  private updateCrowd(elapsed: number): void {
    for (let index = 0; index < this.crowdFigures.length; index += 1) {
      const figure = this.crowdFigures[index];
      const rotation = figure.baseRotation + Math.sin(elapsed * 1.25 + figure.phase) * 0.022;
      const rootY = Math.sin(elapsed * 1.65 + figure.phase) * 0.018;
      this.writeCrowdPart(this.crowdBodyMesh, index, figure, rotation, rootY, 0.45);
      this.writeCrowdPart(this.crowdHeadMesh, index, figure, rotation, rootY, 1.05);
    }
    this.crowdBodyMesh.instanceMatrix.needsUpdate = true;
    this.crowdHeadMesh.instanceMatrix.needsUpdate = true;
  }

  private writeCrowdPart(
    mesh: THREE.InstancedMesh,
    index: number,
    figure: CrowdFigure,
    rotation: number,
    rootY: number,
    localY: number,
  ): void {
    const scaledY = localY * figure.scale;
    this.dummy.position.set(
      figure.x - Math.sin(rotation) * scaledY,
      rootY + Math.cos(rotation) * scaledY,
      figure.z,
    );
    this.dummy.rotation.set(0, 0, rotation);
    this.dummy.scale.setScalar(figure.scale);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(index, this.dummy.matrix);
  }

  private updateAim(delta: number, elapsed: number): void {
    if (this.aimLife <= 0) {
      this.aimGroup.visible = false;
      return;
    }
    this.aimLife = Math.max(0, this.aimLife - delta);
    const breathe = 1 + Math.sin(elapsed * 8.5) * 0.055;
    const powerScale = 0.82 + this.aimPower * 0.48;
    this.aimGroup.scale.setScalar(powerScale * breathe);
    this.aimGroup.rotation.y = elapsed * 0.32;
    const alpha = THREE.MathUtils.clamp(this.aimLife / 0.08, 0, 1);
    this.aimMaterials.forEach((material, index) => {
      material.opacity = (index === 0 ? 0.88 : index === 1 ? 0.54 : 0.76) * alpha;
    });
  }

  private updateConfetti(delta: number): void {
    let changed = false;
    for (let index = 0; index < this.confettiStates.length; index += 1) {
      const state = this.confettiStates[index];
      if (!state.active) continue;
      changed = true;
      state.life -= delta;
      if (state.life <= 0) {
        state.active = false;
        this.writeHiddenInstance(this.confettiMesh, index);
        continue;
      }
      state.velocity.y -= 4.8 * delta;
      state.velocity.multiplyScalar(Math.max(0, 1 - delta * 0.28));
      state.position.addScaledVector(state.velocity, delta);
      state.rotation.x += state.spin.x * delta;
      state.rotation.y += state.spin.y * delta;
      state.rotation.z += state.spin.z * delta;
      const normalized = state.life / state.maxLife;
      const scale = Math.min(1, normalized * 4) * (0.82 + normalized * 0.28);
      this.dummy.position.copy(state.position);
      this.dummy.rotation.copy(state.rotation);
      this.dummy.scale.setScalar(scale);
      this.dummy.updateMatrix();
      this.confettiMesh.setMatrixAt(index, this.dummy.matrix);
    }
    if (changed) this.confettiMesh.instanceMatrix.needsUpdate = true;
  }

  private updatePuffs(delta: number): void {
    let changed = false;
    for (let index = 0; index < this.puffStates.length; index += 1) {
      const state = this.puffStates[index];
      if (!state.active) continue;
      changed = true;
      state.life -= delta;
      if (state.life <= 0) {
        state.active = false;
        this.writeHiddenInstance(this.puffMesh, index);
        continue;
      }
      state.position.addScaledVector(state.velocity, delta);
      state.velocity.multiplyScalar(Math.max(0, 1 - delta * 5));
      const t = 1 - state.life / state.maxLife;
      const scale = state.size * (1 + t * 2.4) * Math.min(1, state.life * 8);
      this.dummy.position.copy(state.position);
      this.dummy.rotation.set(t * 2, t * 1.3, 0);
      this.dummy.scale.setScalar(scale);
      this.dummy.updateMatrix();
      this.puffMesh.setMatrixAt(index, this.dummy.matrix);
    }
    if (changed) this.puffMesh.instanceMatrix.needsUpdate = true;
  }

  private updateRings(delta: number): void {
    for (const ring of this.ringStates) {
      if (!ring.active) continue;
      ring.life -= delta;
      if (ring.life <= 0) {
        ring.active = false;
        ring.mesh.visible = false;
        continue;
      }
      const t = 1 - ring.life / ring.maxLife;
      ring.mesh.scale.setScalar(ring.size * (0.55 + t * 2.35));
      ring.mesh.material.opacity = (1 - t) * 0.72;
    }
  }

  private spawnRing(position: THREE.Vector3, color: string, size: number): void {
    const ring = this.ringStates[this.nextRing];
    this.nextRing = (this.nextRing + 1) % RING_CAPACITY;
    ring.active = true;
    ring.life = 0.52;
    ring.maxLife = 0.52;
    ring.size = size;
    ring.mesh.visible = true;
    ring.mesh.position.copy(position);
    ring.mesh.position.y += 0.03;
    ring.mesh.scale.setScalar(0.5 * size);
    ring.mesh.material.color.set(color);
    ring.mesh.material.opacity = 0.72;
  }

  private writeHiddenInstance(mesh: THREE.InstancedMesh, index: number): void {
    this.dummy.position.set(0, -100, 0);
    this.dummy.rotation.set(0, 0, 0);
    this.dummy.scale.setScalar(0.0001);
    this.dummy.updateMatrix();
    mesh.setMatrixAt(index, this.dummy.matrix);
  }

  private createTube(
    points: THREE.Vector3[],
    radius: number,
    material: THREE.Material,
    tubularSegments: number,
  ): THREE.Mesh {
    const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal');
    return new THREE.Mesh(this.geometry(new THREE.TubeGeometry(curve, tubularSegments, radius, 6, false)), material);
  }

  private makeRoundedPanel(
    width: number,
    height: number,
    depth: number,
    material: THREE.Material,
    radius: number,
  ): THREE.Mesh {
    return new THREE.Mesh(this.createRoundedPanelGeometry(width, height, depth, radius), material);
  }

  private createRoundedPanelGeometry(width: number, height: number, depth: number, radius: number): THREE.ExtrudeGeometry {
    const shape = this.createRoundedRectShape(width, height, radius);
    const geometry = this.geometry(
      new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: true,
        bevelSegments: 2,
        bevelSize: Math.min(radius * 0.22, 0.07),
        bevelThickness: Math.min(depth * 0.38, 0.045),
        curveSegments: 5,
      }),
    );
    geometry.translate(0, 0, -depth / 2);
    return geometry;
  }

  private createRoundedRectShape(width: number, height: number, radius: number): THREE.Shape {
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const r = Math.min(radius, halfWidth, halfHeight);
    const shape = new THREE.Shape();
    shape.moveTo(-halfWidth + r, -halfHeight);
    shape.lineTo(halfWidth - r, -halfHeight);
    shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth, -halfHeight + r);
    shape.lineTo(halfWidth, halfHeight - r);
    shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth - r, halfHeight);
    shape.lineTo(-halfWidth + r, halfHeight);
    shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - r);
    shape.lineTo(-halfWidth, -halfHeight + r);
    shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + r, -halfHeight);
    shape.closePath();
    return shape;
  }

  private createScallopGeometry(width: number, height: number, depth: number): THREE.ExtrudeGeometry {
    const shape = new THREE.Shape();
    shape.moveTo(-width / 2, 0);
    shape.lineTo(width / 2, 0);
    shape.lineTo(width / 2, -height * 0.58);
    shape.quadraticCurveTo(width * 0.25, -height, 0, -height * 0.68);
    shape.quadraticCurveTo(-width * 0.25, -height, -width / 2, -height * 0.58);
    shape.closePath();
    const geometry = this.geometry(
      new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelSize: 0.018, bevelThickness: 0.012, bevelSegments: 1 }),
    );
    geometry.translate(0, 0, -depth / 2);
    return geometry;
  }

  private createPennantGeometry(width: number, height: number): THREE.ShapeGeometry {
    const shape = new THREE.Shape();
    shape.moveTo(-width / 2, 0);
    shape.lineTo(width / 2, 0);
    shape.lineTo(0, -height);
    shape.closePath();
    return this.geometry(new THREE.ShapeGeometry(shape));
  }

  private createStarGeometry(outer: number, inner: number, points: number, depth: number): THREE.ExtrudeGeometry {
    const shape = new THREE.Shape();
    for (let index = 0; index < points * 2; index += 1) {
      const radius = index % 2 === 0 ? outer : inner;
      const angle = -Math.PI / 2 + (index / (points * 2)) * Math.PI * 2;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      if (index === 0) shape.moveTo(x, y);
      else shape.lineTo(x, y);
    }
    shape.closePath();
    const geometry = this.geometry(
      new THREE.ExtrudeGeometry(shape, {
        depth,
        bevelEnabled: true,
        bevelSegments: 1,
        bevelSize: Math.min(0.035, outer * 0.08),
        bevelThickness: Math.min(0.025, depth * 0.3),
      }),
    );
    geometry.translate(0, 0, -depth / 2);
    return geometry;
  }

  private createBowGeometry(): THREE.ExtrudeGeometry {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(-0.31, 0.17);
    shape.lineTo(-0.27, -0.17);
    shape.lineTo(0, 0);
    shape.lineTo(0.31, 0.17);
    shape.lineTo(0.27, -0.17);
    shape.closePath();
    const geometry = this.geometry(new THREE.ExtrudeGeometry(shape, { depth: 0.07, bevelEnabled: true, bevelSize: 0.018, bevelThickness: 0.015 }));
    geometry.translate(0, 0, -0.035);
    return geometry;
  }

  private createFinGeometry(): THREE.ExtrudeGeometry {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(0.37, -0.02);
    shape.lineTo(0.17, 0.53);
    shape.lineTo(0, 0.44);
    shape.closePath();
    const geometry = this.geometry(new THREE.ExtrudeGeometry(shape, { depth: 0.1, bevelEnabled: true, bevelSize: 0.018, bevelThickness: 0.012 }));
    geometry.translate(0, 0, -0.05);
    return geometry;
  }

  private createTicketGeometry(width: number, height: number, depth: number): THREE.ExtrudeGeometry {
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const notch = 0.07;
    const shape = new THREE.Shape();
    shape.moveTo(-halfWidth + 0.08, -halfHeight);
    shape.lineTo(halfWidth - 0.08, -halfHeight);
    shape.quadraticCurveTo(halfWidth, -halfHeight, halfWidth, -halfHeight + 0.08);
    shape.lineTo(halfWidth, -notch);
    shape.absarc(halfWidth, 0, notch, -Math.PI / 2, Math.PI / 2, true);
    shape.lineTo(halfWidth, halfHeight - 0.08);
    shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth - 0.08, halfHeight);
    shape.lineTo(-halfWidth + 0.08, halfHeight);
    shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - 0.08);
    shape.lineTo(-halfWidth, notch);
    shape.absarc(-halfWidth, 0, notch, Math.PI / 2, -Math.PI / 2, true);
    shape.lineTo(-halfWidth, -halfHeight + 0.08);
    shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + 0.08, -halfHeight);
    shape.closePath();
    const geometry = this.geometry(new THREE.ExtrudeGeometry(shape, { depth, bevelEnabled: false, curveSegments: 5 }));
    geometry.translate(0, 0, -depth / 2);
    return geometry;
  }

  private canvasTexture(
    width: number,
    height: number,
    draw: (context: CanvasRenderingContext2D, width: number, height: number) => void,
  ): THREE.CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to create CarnivalScene canvas texture.');
    draw(context, width, height);
    const texture = this.texture(new THREE.CanvasTexture(canvas));
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = true;
    texture.needsUpdate = true;
    return texture;
  }

  private drawCanvasStar(
    context: CanvasRenderingContext2D,
    centerX: number,
    centerY: number,
    outer: number,
    inner: number,
    points: number,
  ): void {
    context.beginPath();
    for (let index = 0; index < points * 2; index += 1) {
      const radius = index % 2 === 0 ? outer : inner;
      const angle = -Math.PI / 2 + (index / (points * 2)) * Math.PI * 2;
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      if (index === 0) context.moveTo(x, y);
      else context.lineTo(x, y);
    }
    context.closePath();
  }

  private geometry<T extends THREE.BufferGeometry>(geometry: T): T {
    this.ownedGeometries.add(geometry);
    return geometry;
  }

  private material<T extends THREE.Material>(material: T): T {
    this.ownedMaterials.add(material);
    return material;
  }

  private texture<T extends THREE.Texture>(texture: T): T {
    this.ownedTextures.add(texture);
    return texture;
  }
}
